import { Component, Input, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { concat, Observable, of, Subject } from "rxjs";
import { AutocompleteService } from "../../../services/autocomplete.service";
import {
    catchError,
    debounceTime,
    distinctUntilChanged,
    map,
    startWith,
    switchMap,
    takeUntil,
    tap
} from "rxjs/operators";
import { ActivatedRoute, Router } from "@angular/router";
import { NgSelectComponent } from "@ng-select/ng-select";

@Component({
    selector: "app-select-http-filter",
    templateUrl: "./select-http-filter.component.html",
    styleUrls: ["./select-http-filter.component.scss"]
})
export class SelectHttpFilterComponent implements OnInit, OnDestroy {
    @ViewChild(NgSelectComponent) select: NgSelectComponent;

    input$ = new Subject<string>();

    @Input() url: string;
    @Input() placeholder = "";
    @Input() field;
    @Input() labelField: string;
    @Input() valueField: string;
    @Input() multiple = false;

    isLoading = false;
    items$: Observable<any[]>;
    selectedItems: any[];

    unsubscribe$ = new Subject();

    constructor(
        private autocompleteService: AutocompleteService,
        private route: ActivatedRoute,
        private router: Router
    ) {}

    ngOnInit() {
        const ids = this.route.snapshot.queryParamMap.get(this.field);
        if (ids && ids.length > 0) this.loadInitial(ids);

        this.items$ = concat(
            of([]),
            this.input$.pipe(
                startWith(""),
                distinctUntilChanged(),
                debounceTime(400),
                tap(() => (this.isLoading = true)),
                switchMap(value => this._filter(value))
            )
        );

        this.route.queryParamMap
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(params => {
                const routeIds = params.get(this.field);
                if (!routeIds || routeIds.length === 0) {
                    this.selectedItems = [];
                }
            });
    }

    ngOnDestroy() {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    async loadInitial(ids: string) {
        const items = await this.autocompleteService
            .findByIds(this.url, ids)
            .toPromise();
        this.selectedItems = items.map(item => ({
            label: item[this.labelField],
            value: item[this.valueField]
        }));
        if (!this.multiple) this.selectedItems = (this.selectedItems || [])[0];
    }

    private _filter(value: string) {
        const filterValue = (value || "").toLowerCase();
        return this.autocompleteService
            .search(this.url, filterValue, this.valueField, this.labelField)
            .pipe(
                map(val =>
                    val.map(item => ({
                        label: item[this.labelField],
                        value: item[this.valueField]
                    }))
                ),
                catchError(() => of([])),
                tap(() => (this.isLoading = false))
            );
    }

    async valueChanged(items) {
        if (!this.multiple) items = items ? [items] : [];
        items = items.map(item => item.value);
        const queryParams: any = {};
        queryParams[this.field] = items.join(",") || null;
        await this.router.navigate([], {
            relativeTo: this.route,
            queryParams,
            queryParamsHandling: "merge"
        });
    }
}
